import { useEffect, useState } from "react";
import type { AssetView, AssetVisibility } from "@seedance/contracts";
import type { UploadAsset } from "../../api/upload-transport.js";
import { canvasCopy } from "../../copy/zh-CN.js";
import { offsetCanvasPoint, type Point } from "../asset-drop.js";
import { classifyUploadFailure, uploadFailureMessage, type UploadFailure } from "./upload-errors.js";

export type UploadStatus =
  | "queued"
  | "uploading"
  | "processing"
  | "complete"
  | "failed"
  | "cancelled";

export interface UploadQueueItem {
  id: string;
  file: File;
  canvasPoint: Point;
  status: UploadStatus;
  progress: number;
  error?: string | undefined;
  failure?: UploadFailure | undefined;
  asset?: AssetView;
}

export interface UploadQueueController {
  enqueue(files: readonly File[], anchor: Point): string[];
  retry(itemId: string): void;
  cancel(itemId: string): void;
  getItems(): UploadQueueItem[];
  subscribe(listener: (items: UploadQueueItem[]) => void): () => void;
  dispose(): void;
}

export function createUploadQueueController({
  uploadAsset,
  projectId,
  visibility,
  onComplete,
  onUploaded,
  completedRetentionMs = 1_800
}: {
  uploadAsset: UploadAsset;
  projectId?: string;
  visibility?: () => AssetVisibility;
  onComplete(asset: AssetView, canvasPoint: Point): void | Promise<void>;
  /** Non-blocking library invalidation; failure must not cause a duplicate upload. */
  onUploaded?(asset: AssetView): void | Promise<void>;
  completedRetentionMs?: number;
}): UploadQueueController {
  let items: UploadQueueItem[] = [];
  let disposed = false;
  const listeners = new Set<(value: UploadQueueItem[]) => void>();
  const abortControllers = new Map<string, AbortController>();
  const removalTimers = new Map<string, ReturnType<typeof setTimeout>>();
  const completedItems = new Set<string>();
  let completionQueue: Promise<void> = Promise.resolve();

  const snapshot = () => items.map((item) => ({ ...item }));
  const notify = () => {
    const value = snapshot();
    listeners.forEach((listener) => listener(value));
  };
  const update = (itemId: string, change: Partial<UploadQueueItem>) => {
    items = items.map((item) =>
      item.id === itemId ? { ...item, ...change } : item
    );
    notify();
  };
  const current = (itemId: string) => items.find((item) => item.id === itemId);
  const scheduleRemoval = (itemId: string) => {
    const existingTimer = removalTimers.get(itemId);
    if (existingTimer) clearTimeout(existingTimer);
    const timer = setTimeout(() => {
      removalTimers.delete(itemId);
      if (disposed || current(itemId)?.status !== "complete") return;
      items = items.filter((item) => item.id !== itemId);
      notify();
    }, Math.max(0, completedRetentionMs));
    removalTimers.set(itemId, timer);
  };

  const complete = (itemId: string, asset: AssetView): Promise<void> => {
    const previous = completionQueue;
    const operation = previous.then(
      () => completeInOrder(itemId, asset),
      () => completeInOrder(itemId, asset)
    );
    completionQueue = operation.catch(() => undefined);
    return operation;
  };

  const completeInOrder = async (itemId: string, asset: AssetView) => {
    const item = current(itemId);
    if (
      disposed ||
      !item ||
      completedItems.has(itemId) ||
      item.status === "cancelled" ||
      item.status === "processing"
    ) {
      return;
    }
    update(itemId, {
      status: "processing",
      progress: 1,
      error: undefined,
      failure: undefined,
      asset
    });
    try {
      await onComplete(asset, item.canvasPoint);
      if (disposed || current(itemId)?.status === "cancelled") return;
      completedItems.add(itemId);
      update(itemId, { status: "complete", progress: 1, asset });
      scheduleRemoval(itemId);
    } catch (_error) {
      if (disposed || current(itemId)?.status === "cancelled") return;
      update(itemId, {
        status: "failed",
        progress: 1,
        error: uploadFailureMessage({ reason: "placement" }),
        failure: { reason: "placement" },
        asset
      });
    }
  };

  const start = async (itemId: string) => {
    const item = current(itemId);
    if (
      disposed ||
      !item ||
      completedItems.has(itemId) ||
      (item.status !== "queued" && item.status !== "failed")
    ) {
      return;
    }
    const abortController = new AbortController();
    abortControllers.set(itemId, abortController);
    update(itemId, { status: "uploading", progress: 0, error: undefined, failure: undefined });
    try {
      const asset = await uploadAsset({
        file: item.file,
        ...(projectId ? { projectId } : {}),
        ...(visibility ? { visibility: visibility() } : {}),
        signal: abortController.signal,
        onProgress: (progress) => {
          if (current(itemId)?.status === "uploading") {
            update(itemId, {
              progress: Math.max(0, Math.min(1, progress))
            });
          }
        }
      });
      if (disposed || current(itemId)?.status === "cancelled") return;
      // Observe successful storage independently of canvas placement and its retry.
      void Promise.resolve().then(() => {
        if (!disposed) return onUploaded?.(asset);
      }).catch(() => undefined);
      await complete(itemId, asset);
    } catch (error) {
      if (disposed || current(itemId)?.status === "cancelled") return;
      const failure = abortController.signal.aborted ? undefined : classifyUploadFailure(error);
      update(itemId, {
        status: abortController.signal.aborted ? "cancelled" : "failed",
        error: failure ? uploadFailureMessage(failure) : undefined,
        failure
      });
    } finally {
      abortControllers.delete(itemId);
    }
  };

  return {
    enqueue(files, anchor) {
      if (disposed || files.length === 0) return [];
      const added = files.map<UploadQueueItem>((file, index) => ({
        id: crypto.randomUUID(),
        file,
        canvasPoint: offsetCanvasPoint(anchor, index),
        status: "queued",
        progress: 0
      }));
      items = [...items, ...added];
      notify();
      added.forEach(({ id }) => queueMicrotask(() => void start(id)));
      return added.map(({ id }) => id);
    },
    retry(itemId) {
      const item = current(itemId);
      if (!item || item.status !== "failed") return;
      if (item.asset) void complete(itemId, item.asset);
      else void start(itemId);
    },
    cancel(itemId) {
      const item = current(itemId);
      if (!item || (item.status !== "queued" && item.status !== "uploading")) return;
      update(itemId, { status: "cancelled", error: undefined });
      abortControllers.get(itemId)?.abort();
    },
    getItems: snapshot,
    subscribe(listener) {
      listeners.add(listener);
      listener(snapshot());
      return () => listeners.delete(listener);
    },
    dispose() {
      disposed = true;
      abortControllers.forEach((controller) => controller.abort());
      abortControllers.clear();
      removalTimers.forEach((timer) => clearTimeout(timer));
      removalTimers.clear();
      listeners.clear();
    }
  };
}

const uploadLabels: Record<UploadStatus, string> = {
  queued: canvasCopy.uploadQueued,
  uploading: canvasCopy.uploading,
  processing: canvasCopy.uploadProcessing,
  complete: canvasCopy.uploadComplete,
  failed: canvasCopy.uploadFailed,
  cancelled: canvasCopy.uploadCancelled
};

export function shouldUploadQueueCapturePointerEvents(
  items: readonly Pick<UploadQueueItem, "status">[]
): boolean {
  return items.some(({ status }) =>
    status === "queued" || status === "uploading" || status === "failed"
  );
}

export function UploadQueue({ controller }: { controller: UploadQueueController }) {
  const [items, setItems] = useState(() => controller.getItems());
  useEffect(() => controller.subscribe(setItems), [controller]);
  if (items.length === 0) return null;

  return (
    <aside
      className={`studio-upload-queue${shouldUploadQueueCapturePointerEvents(items) ? "" : " studio-upload-queue--passive"}`}
      aria-label={canvasCopy.uploadQueue}
    >
      <strong>{canvasCopy.uploadQueue}</strong>
      <ol>
        {items.map((item, index) => {
          const accessibleName = `${item.file.name} ${canvasCopy.uploadItem} ${index + 1}`;
          return (
          <li key={item.id}>
            <span title={item.file.name}>{item.file.name}</span>
            <small role={item.status === "failed" ? "alert" : undefined}>{item.status === "failed" ? uploadFailureMessage(item.failure) : uploadLabels[item.status]}</small>
            <progress max={1} value={item.progress} aria-label={`${accessibleName} ${canvasCopy.uploadProgress}`} />
            {item.status === "failed" ? (
              <button type="button" aria-label={`${accessibleName} ${canvasCopy.retryUpload}`} onClick={() => controller.retry(item.id)}>{canvasCopy.retryUpload}</button>
            ) : null}
            {item.status === "queued" || item.status === "uploading" ? (
              <button type="button" aria-label={`${accessibleName} ${canvasCopy.cancelUpload}`} onClick={() => controller.cancel(item.id)}>{canvasCopy.cancelUpload}</button>
            ) : null}
          </li>
          );
        })}
      </ol>
    </aside>
  );
}
